// Notifications page
// Reference: spec.md US8 - notifications for issue updates

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui';
import { api } from '../../services/api';
import type { Notification } from '@propman/shared';

// Event type labels and icons
const EVENT_TYPE_CONFIG = {
  new_issue: {
    label: 'New Issue',
    icon: (
      <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-blue-50',
  },
  status_changed: {
    label: 'Status Update',
    icon: (
      <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
    bgColor: 'bg-amber-50',
  },
  new_comment: {
    label: 'New Comment',
    icon: (
      <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    bgColor: 'bg-green-50',
  },
  issue_assigned: {
    label: 'Issue Assigned',
    icon: (
      <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    bgColor: 'bg-purple-50',
  },
};

function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 7) {
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return 'Just now';
}

export function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, [showUnreadOnly]);

  async function loadNotifications(loadMore = false) {
    if (!loadMore) {
      setIsLoading(true);
      setPage(1);
    }

    try {
      const currentPage = loadMore ? page + 1 : 1;
      const response = await api.get<{
        notifications: Notification[];
        meta: { hasMore: boolean };
        unreadCount: number;
      }>('/notifications', {
        page: currentPage,
        pageSize: 20,
        unreadOnly: showUnreadOnly,
      });

      if (loadMore) {
        setNotifications((prev) => [...prev, ...response.notifications]);
        setPage(currentPage);
      } else {
        setNotifications(response.notifications);
      }

      setHasMore(response.meta.hasMore);
      setUnreadCount(response.unreadCount);
      setError(null);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleToggleRead(notification: Notification) {
    const endpoint = notification.isRead
      ? `/notifications/${notification.id}/unread`
      : `/notifications/${notification.id}/read`;

    try {
      const response = await api.patch<{ notification: Notification }>(endpoint, {});

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? response.notification : n))
      );

      // Update unread count
      setUnreadCount((prev) => prev + (notification.isRead ? 1 : -1));
    } catch (err) {
      console.error('Failed to update notification:', err);
    }
  }

  async function handleMarkAllRead() {
    setIsMarkingAll(true);
    try {
      await api.post('/notifications/mark-all-read', {});

      // Update local state
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, isRead: true, readAt: new Date() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    } finally {
      setIsMarkingAll(false);
    }
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="mt-1 text-sm text-gray-600">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="secondary"
            onClick={handleMarkAllRead}
            isLoading={isMarkingAll}
          >
            Mark all as read
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-4">
        <button
          type="button"
          onClick={() => setShowUnreadOnly(false)}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            !showUnreadOnly
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          All
        </button>
        <button
          type="button"
          onClick={() => setShowUnreadOnly(true)}
          className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            showUnreadOnly
              ? 'bg-primary-100 text-primary-700'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          Unread only
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="secondary" size="sm" className="mt-2" onClick={() => loadNotifications()}>
            Try Again
          </Button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading notifications...</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && notifications.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {showUnreadOnly ? 'No unread notifications' : 'No notifications yet'}
          </h3>
          <p className="mt-2 text-gray-600">
            {showUnreadOnly
              ? "You're all caught up!"
              : "You'll be notified when there are updates to your issues."}
          </p>
        </div>
      )}

      {/* Notifications list */}
      {notifications.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200 overflow-hidden">
          {notifications.map((notification) => {
            const config = EVENT_TYPE_CONFIG[notification.eventType] || EVENT_TYPE_CONFIG.status_changed;

            return (
              <div
                key={notification.id}
                className={`p-4 flex items-start gap-4 transition-colors ${
                  !notification.isRead ? 'bg-primary-50/30' : ''
                }`}
              >
                {/* Icon */}
                <div className={`p-2 rounded-full ${config.bgColor} flex-shrink-0`}>
                  {config.icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!notification.isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                    {notification.title}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>

                  {/* Actions */}
                  <div className="mt-2 flex items-center gap-3">
                    {notification.issueId && (
                      <Link
                        to={`/issues/${notification.issueId}`}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View Issue
                      </Link>
                    )}
                  </div>
                </div>

                {/* Time and Read/Unread toggle - aligned */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {formatRelativeTime(notification.createdAt)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleToggleRead(notification)}
                    className={`p-1.5 rounded-md transition-colors ${
                      notification.isRead
                        ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        : 'text-primary-600 hover:text-primary-700 hover:bg-primary-50'
                    }`}
                    title={notification.isRead ? 'Mark as unread' : 'Mark as read'}
                  >
                    {notification.isRead ? (
                      // Empty checkbox (unchecked)
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                      </svg>
                    ) : (
                      // Filled checkbox (checked)
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Load more */}
      {hasMore && !isLoading && (
        <div className="mt-6 text-center">
          <Button variant="secondary" onClick={() => loadNotifications(true)}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
